import React, { useCallback, useMemo, useState } from 'react';

import { messageConfig } from 'config/appMessageConfig';
import Loader from 'components/molecules/Loader';

interface EmbeddedFrameProps {
    url: string;
    title: string;
    onLoad?: () => void;
    onError?: () => void;
    testIdPrefix?: string;
}

const FRAME_SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-popups';

// Only https is embeddable. `javascript:`, `data:`, plain http and unparseable strings are
// rejected here so no <iframe> is ever mounted for them. No host allow-list is consulted:
// the URL is trusted to the extent that it came from the tenant config record.
const isEmbeddableUrl = (url: string): boolean => {
    try {
        return new URL(url).protocol === 'https:';
    } catch {
        return false;
    }
};

const EmbeddedFrame = ({ url, title, onLoad, onError, testIdPrefix = 'demo_iframe_screen' }: EmbeddedFrameProps) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);

    const isEmbeddable = useMemo(() => isEmbeddableUrl(url), [url]);

    const handleLoad = useCallback(() => {
        setIsLoaded(true);
        onLoad?.();
    }, [onLoad]);

    const handleError = useCallback(() => {
        setIsLoaded(true);
        setHasFailed(true);
        onError?.();
    }, [onError]);

    if (!isEmbeddable || hasFailed) {
        return (
            <div
                className="h-full w-full flex justify-center items-center"
                data-testid={`${testIdPrefix}_error`}
                role="alert"
            >
                {messageConfig.errorMessages.iframeScreenUnavailable}
            </div>
        );
    }

    return (
        // `flex-1 min-h-0` fills a flex-column parent; `h-full` covers a plain block parent so the
        // component still sizes when mounted standalone. Whichever applies, the box is definite.
        <div className="h-full w-full relative flex flex-col flex-1 min-h-0">
            {!isLoaded && (
                <div className="absolute inset-0" data-testid={`${testIdPrefix}_loader`}>
                    <Loader size="default" />
                </div>
            )}
            <iframe
                src={url}
                title={title}
                data-testid={`${testIdPrefix}_frame`}
                sandbox={FRAME_SANDBOX}
                referrerPolicy="no-referrer"
                onLoad={handleLoad}
                onError={handleError}
                // Flexing rather than taking a percentage: nothing here resolves against an
                // ancestor height, so no link in the chain can collapse it back to 150px.
                style={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    width: '100%',
                    margin: 0,
                    padding: 0,
                    border: 'none',
                    display: 'block',
                }}
            />
        </div>
    );
};

export default EmbeddedFrame;
